import getCDNURL from '@pickleballinc/ui/components/global/utils/getCDNURL';
import dayjs from 'dayjs';

import { MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum, GameCompletedEnum } from '@/domain/constants/constants';

import { SubMatch } from '@/infrastructure/network/dto/matches/matchesDto';
import { getGameStatusKey } from '@/utils/helpers';

/**
 * Formats a date string into a human-readable format
 *
 * @param plannedStartDate - ISO date string or null/undefined
 * @returns Formatted date string in format "MMM D, YYYY | h:mm A"
 *
 * @example
 * // Returns "Jan 1, 2024 | 3:30 pm"
 * formatMatchDate("2024-01-01T15:30:00")
 *
 * // Returns ""
 * formatMatchDate(null)
 * formatMatchDate(undefined)
 */
export const formatMatchDate = (plannedStartDate?: string | null): string => {
	const dateToFormat = plannedStartDate;

	if (!dateToFormat) return '';

	return dayjs(dateToFormat).format('MMM D, YYYY | h:mm A');
};

export const areAllMatchesCompleted = (matches: SubMatch[]): boolean => {
	return matches.every((match) => match.matchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS]);
};

// TODO: refactor this, may be a better way to do this..
export const calculateFinalScore = (
	gameResults: Array<[number, number]>,
	match?: SubMatch
): { teamOneFinalScore: number; teamTwoFinalScore: number } => {
	// If match is not completed, return 0-0
	if (!match || match.matchStatus !== MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS]) {
		return { teamOneFinalScore: 0, teamTwoFinalScore: 0 };
	}

	let teamOneWins = 0;
	let teamTwoWins = 0;

	// Process normal game results based on scores
	gameResults.forEach(([scoreOne, scoreTwo], index) => {
		const gameStatusKey = getGameStatusKey(index + 1) as keyof SubMatch;
		const gameStatus = match[gameStatusKey];

		if (
			gameStatus === GameCompletedEnum.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE.toString() ||
			gameStatus === GameCompletedEnum.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE.toString() ||
			gameStatus === GameCompletedEnum.PLAYED_COMPLETED_TYPE.toString() ||
			gameStatus === GameCompletedEnum.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE.toString() ||
			gameStatus === GameCompletedEnum.RETIREMENT_TEAM_TWO_GAME_COMPLETED_TYPE.toString()
		) {
			if (scoreOne > scoreTwo) {
				teamOneWins++;
			} else if (scoreOne < scoreTwo) {
				teamTwoWins++;
			}
		}
	});

	// Check for forfeits and retirements
	const statusFields = ['gameOneStatus', 'gameTwoStatus', 'gameThreeStatus', 'gameFourStatus', 'gameFiveStatus'];

	statusFields.forEach((statusField, index) => {
		const statusValue = match[statusField as keyof typeof match];

		// Skip games that are already counted through scores
		const [scoreOne, scoreTwo] = gameResults[index] || [0, 0];
		if (scoreOne > 0 || scoreTwo > 0) return;

		// Process forfeit statuses
		if (statusValue === GameCompletedEnum.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE.toString()) {
			// Team one forfeited, add win to team two
			teamTwoWins++;
		} else if (statusValue === GameCompletedEnum.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE.toString()) {
			// Team two forfeited, add win to team one
			teamOneWins++;
		}

		// Process retirement statuses
		else if (statusValue === GameCompletedEnum.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE.toString()) {
			// Team one retired, add win to team two
			teamTwoWins++;
		} else if (statusValue === GameCompletedEnum.RETIREMENT_TEAM_TWO_GAME_COMPLETED_TYPE.toString()) {
			// Team two retired, add win to team one
			teamOneWins++;
		}
	});

	return { teamOneFinalScore: teamOneWins, teamTwoFinalScore: teamTwoWins };
};

export const getProfileImageUrl = (profileImage: string | undefined): string => {
	if (profileImage) {
		const imageUrl = getCDNURL(profileImage, 100, 100);
		return imageUrl;
	} else {
		const defaultImageUrl = `${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleball-app/static/profile-image.svg`;
		return getCDNURL(defaultImageUrl, 500, 500);
	}
};

/**
 * The calculateAge function calculates the age based on the date of birth (dob).
 *
 * @param dob - Date of birth in string format (e.g., "2019-10-04T00:00:00Z").
 * @returns - Age as a string in years.
 */
export const calculateAge = (dob: string | null): string | null => {
	if (!dob) {
		return null;
	}
	const birthDate = new Date(dob);
	const ageDiff = Date.now() - birthDate.getTime();
	const ageDate = new Date(ageDiff);
	return Math.abs(ageDate.getUTCFullYear() - 1970).toString();
};

/**
 * Formats the score format title to a shorter, user-friendly version.
 *
 * - For formats like "2 games guaranteed - All 2 games to 11 win by 1",
 *   returns "Guaranteed 2 to 11 by 1".
 * - Otherwise, shortens common phrases or returns the original if no match.
 */
export const formatScoreFormatTitle = (scoreFormatTitle: string | null): string => {
	if (!scoreFormatTitle) return '';

	// Generic pattern: e.g. "2 games guaranteed - All 2 games to 11 win by 1"
	const guaranteedRegex = /^(\d+)\s+games guaranteed\s*-\s*All\s*\d+\s*games to\s*(\d+)\s*win by\s*(\d+)/i;
	const guaranteedMatch = scoreFormatTitle.match(guaranteedRegex);

	if (guaranteedMatch) {
		const guaranteed = guaranteedMatch[1];
		const to = guaranteedMatch[2];
		const by = guaranteedMatch[3];
		return `Guaranteed ${guaranteed} to ${to} by ${by}`;
	}

	// If no keywords to shorten, return as is
	if (!scoreFormatTitle.includes('out of') && !scoreFormatTitle.includes('All games to')) {
		return scoreFormatTitle;
	}

	// Shorten common phrases
	return scoreFormatTitle.replace('out of', 'of').replace('All games to', 'all to').replace('win by', 'by').trim();
};

export const areAllPlayersSelectedForMatch = (matches: SubMatch[]): boolean => {
	return matches
		.filter((match) => !match.isTieBreaker && match.matchStatus === MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE)
		.every((match) => {
			if (match.formatTitle === 'Doubles') {
				return Boolean(match.teamOnePlayerOneUuid && match.teamOnePlayerTwoUuid && match.teamTwoPlayerOneUuid && match.teamTwoPlayerTwoUuid);
			}
			return Boolean(match.teamOnePlayerOneUuid && match.teamTwoPlayerOneUuid);
		});
};

export const formatCourtTitles = (matches: Array<{ courtTitle: string | null }>): string => {
	const uniqueCourtTitles = [...new Set(matches.map((match) => match.courtTitle).filter(Boolean))];

	if (uniqueCourtTitles.length === 0) return '';
	if (uniqueCourtTitles.length === 1) return `Court: ${uniqueCourtTitles[0]}`;

	return `Courts: ${uniqueCourtTitles.join(', ')}`;
};

export const isTeamLeagueUsingPods = (matches: SubMatch[]): boolean => {
	return matches.some((match) => !!match.teamLeagueSeasonDivisionPodUuid);
};
